import {
  ActionBar,
  ChakraBox,
  ChakraFlex,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';
import { useGetModalityTreatmentAttributesQuery } from '~/store/services/treatmentAttributes';
import { ActiveCarePlan } from './ActiveCarePlan';
import { useState } from 'react';
import { AddProblemModal } from './AddProblemModal';
import { AddToCarePlanMenu } from './AddToCarePlanMenu';
import { CarePlanProblemDialogManager } from './CarePlanProblemDialogManager';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import AddQoLGoalModal from './AddQoLGoalModal';
import { CreateCarePlanModal } from '~/components/maud/patients/profile/tabs/careplan/CreateCarePlanModal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { VoidCarePlanModal } from '~/components/maud/patients/profile/tabs/careplan/VoidCarePlanModal';
import ModalityHasChangedBanner from './ModalityHasChangedBanner';
import { getCarePlanStatusMessage } from '~/lib/util/carePlanStatus.util';
import { getOpenTreatmentAttribute } from '~/lib/util/treatmentEpisodes.util';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { useActivePatientCarePlan } from '~/lib/hooks/care-team/useActivePatientCarePlan';
import { usePatientActivePrimaryDiagnosis } from '~/lib/hooks/care-team/useActivePatientDiagnosis';

const { neutral } = EquipUIFireflyV2Theme.colors;

type Props = {
  patientId: string;
};

export const PatientCarePlans = ({ patientId }: Props) => {
  const { carePlanM4ModalityOptional: isModalityOptional } =
    useFlags<LDFlags>();

  const { trackEvent } = useAnalytics();

  const {
    isLoading: isGetActivePatientCarePlanLoading,
    activeCarePlan,
    activeTreatmentEpisode,
    error: getActivePatientCarePlanError,
  } = useActivePatientCarePlan(patientId);

  const {
    isLoading: isGetActivePrimaryDiagnosisLoading,
    activePrimaryDiagnosis,
  } = usePatientActivePrimaryDiagnosis(patientId);

  const { data: modalities, isLoading: isGetModalitiesLoading } =
    useGetModalityTreatmentAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );

  const [isCreateCarePlanModalOpen, setIsCreateCarePlanModalOpen] =
    useState(false);
  const [isVoidCarePlanModalOpen, setIsVoidCarePlanModalOpen] = useState(false);
  const [isAddProblemModalOpen, setIsAddProblemModalOpen] = useState(false);
  const [problemTypeCode, setProblemTypeCode] =
    useState<ProblemTypeCode | null>(null);
  const [isAddQoLGoalModalOpen, setIsAddQoLGoalModalOpen] = useState(false);

  const openModality = getOpenTreatmentAttribute(modalities);
  const hasActiveModality = !!openModality;
  const hasModalityChanged =
    !!activeCarePlan?.treatmentEpisodeModality &&
    openModality &&
    openModality.id !== activeCarePlan.treatmentEpisodeModality.id;

  const carePlanMenuOptions: FireflyKebabMenuItem[] = [
    {
      label: 'Create new care plan',
      leftIcon: FAIconName.penRegular,
      onClick: () => {
        trackEvent(CarePlanEvents.clickOnCreateNewCarePlanKebabMenuOption);
        setIsCreateCarePlanModalOpen(true);
      },
    },
    {
      label: 'Void current care plan',
      leftIcon: FAIconName.trashRegular,
      leftIconColor: 'critical.base',
      onClick: () => {
        trackEvent(CarePlanEvents.clickOnVoidCurrentCarePlanKebabMenuOption);
        setIsVoidCarePlanModalOpen(true);
      },
    },
  ];

  if (
    isGetActivePatientCarePlanLoading ||
    isGetModalitiesLoading ||
    isGetActivePrimaryDiagnosisLoading
  ) {
    return (
      <SkeletonLoader
        height="600px"
        id="treatment-pathway-care-plan-loading"
        width="100%"
      />
    );
  }

  const canAddToCarePlan =
    !!activeTreatmentEpisode &&
    activePrimaryDiagnosis &&
    (isModalityOptional || hasActiveModality) &&
    !getActivePatientCarePlanError &&
    !hasModalityChanged;

  return (
    <ChakraFlex flexDirection="column" gap="12px" marginTop="18px" width="100%">
      <ChakraFlex alignItems="center" justifyContent="space-between">
        <Text variant="h4">Care plan</Text>
        <ChakraFlex alignItems="center">
          <AddToCarePlanMenu
            buttonId="add-to-treatment-pathway-btn"
            isDisabled={!canAddToCarePlan}
            onLegacyEdProblemClick={() => setIsAddProblemModalOpen(true)}
            onProblemTypeSelected={setProblemTypeCode}
            onQoLGoalClick={() => setIsAddQoLGoalModalOpen(true)}
          />
          {activeCarePlan && (
            <ChakraBox marginLeft="12px">
              <FireflyKebabMenu
                aria-label="Care plan menu"
                onMenuOpen={() =>
                  trackEvent(CarePlanEvents.clickOnCarePlanKebabMenu)
                }
                options={carePlanMenuOptions}
              />
            </ChakraBox>
          )}
        </ChakraFlex>
      </ChakraFlex>

      {hasModalityChanged && (
        <ModalityHasChangedBanner
          onClick={() => setIsCreateCarePlanModalOpen(true)}
        />
      )}

      {getActivePatientCarePlanError ? (
        <ChakraBox marginY="12px">
          <ActionBar
            barIcon={FAIconName.exclamationTriangleRegular}
            id="care-plan__error-banner"
            text="We were unable to load the patient's care plan. Please refresh the page or try again later."
            variant="critical"
          />
        </ChakraBox>
      ) : (
        <>
          {!activeCarePlan && (
            <ChakraFlex
              alignItems="center"
              border={`1px dashed ${neutral.line}`}
              borderRadius="12px"
              justifyContent="center"
              padding="18px"
            >
              <Text color="neutral.secondary">
                {getCarePlanStatusMessage({
                  activeTreatmentEpisode,
                  hasActiveModality,
                  hasActivePrimaryDiagnosis: !!activePrimaryDiagnosis,
                  isModalityOptional,
                })}
              </Text>
            </ChakraFlex>
          )}
          {activeCarePlan && (
            <ActiveCarePlan
              carePlan={activeCarePlan}
              hasModalityChanged={hasModalityChanged}
              treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
            />
          )}
        </>
      )}
      {isCreateCarePlanModalOpen && (
        <CreateCarePlanModal
          onClose={() => setIsCreateCarePlanModalOpen(false)}
          treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
        />
      )}
      {isVoidCarePlanModalOpen && (
        <VoidCarePlanModal
          onClose={() => setIsVoidCarePlanModalOpen(false)}
          treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
          treatmentPathwayId={activeCarePlan?.id}
        />
      )}
      {isAddProblemModalOpen && (
        <AddProblemModal
          activeCarePlan={activeCarePlan}
          closeModal={() => setIsAddProblemModalOpen(false)}
          patientId={patientId}
          problemTypeCode={ProblemTypeCode.EATING_DISORDER}
          treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
        />
      )}
      {problemTypeCode && (
        <CarePlanProblemDialogManager
          onClose={() => setProblemTypeCode(null)}
          patientId={patientId}
          problemTypeCode={problemTypeCode}
          treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
        />
      )}
      {isAddQoLGoalModalOpen && (
        <AddQoLGoalModal
          onClose={() => setIsAddQoLGoalModalOpen(false)}
          treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
          treatmentPathwayId={activeCarePlan?.id}
        />
      )}
    </ChakraFlex>
  );
};
